/**
 * 首页内容。
 *
 * 所有数字都来自仓库内的事实来源（`platform/README.md`、`platform/docs/`、`platform/shared/src/`），
 * 这里不做任何放大或估算。
 */
import type { IconName } from './icons';

export interface ActionLink {
  label: string;
  to: string;
  /** 站外链接（平台、GitHub）加 target=_blank 与 rel */
  external?: boolean;
  /** 主按钮 / 次按钮 / 纯文字链接 */
  variant: 'primary' | 'secondary' | 'ghost';
  /** 与 to 搭配的锚点说明，仅用于无障碍描述 */
  hint?: string;
}

export interface HeroContent {
  eyebrow: string;
  /** 标题前半段，正常颜色。 */
  titleLead: string;
  /** 标题后半段，上品牌蓝 —— 阿里云式的双色标题。 */
  titleHighlight: string;
  lead: string;
  actions: readonly ActionLink[];
  note: string;
}

export const HERO: HeroContent = {
  eyebrow: '低空经济 · 无人机运营监管',
  titleLead: '低空运营监管平台，',
  titleHighlight: '让每一次飞行都有据可查',
  lead: 'Havorix™ 是一套面向低空经济的无人机运营监管平台。机队状态、单机实时数据、账号权限、告警阈值与操作记录汇聚于同一界面；我们采用浏览器直连机体的数据链路，遥测不经过平台中转，因此机队规模扩大时也不会产生额外的带宽开销。',
  actions: [
    { label: '进入平台', to: 'https://havorix.work', external: true, variant: 'primary' },
    { label: '申请试用', to: '/pricing#trial', variant: 'secondary' },
    { label: '了解平台能力', to: '/platform', variant: 'ghost' },
  ],
  note: '平台内置 6 台模拟机体。在尚未接入真机的情况下，您也可以完整走通实时遥测、告警与指令下发的全流程。',
};

export interface Highlight {
  icon: IconName;
  title: string;
  text: string;
  to: string;
}

/** 首屏下方的四宫格卖点，对应阿里云首页紧跟 Hero 的那一排产品亮点。 */
export const HOME_HIGHLIGHTS: readonly Highlight[] = [
  {
    icon: 'link',
    title: '浏览器直连机体',
    text: '遥测由浏览器向机体直接获取，平台不转发、不存储，机队扩容不产生额外的带宽开销。',
    to: '/platform#realtime',
  },
  {
    icon: 'bell',
    title: '三级阈值与三层覆盖',
    text: '注意、警告、严重各自对应明确的处置要求，并可按机队或按单机逐层覆盖。',
    to: '/platform#thresholds',
  },
  {
    icon: 'cpu',
    title: '内置 6 台模拟机体',
    text: '尚未配备真机也可以跑通注册、总览、指令与告警的完整流程，并可注入断链验证降级。',
    to: '/platform#access',
  },
  {
    icon: 'audit',
    title: '审计留档 180 天',
    text: '何人、何时、下发何指令、变更何配置，均可按时间与操作人检索。',
    to: '/platform#modules',
  },
];

export const HOME_COMING_SOON = {
  eyebrow: '即将上线',
  title: '四款新产品正在规划中',
  lead: '它们与监管平台共享同一套数据口径，分别覆盖设备接入、作业窗口判断、定位精度与飞控底层。目前尚无发布时间与报价。',
  action: { label: '查看全部产品', to: '/products#coming-soon', variant: 'secondary' },
} as const;

export interface FactItem {
  value: string;
  unit?: string;
  label: string;
  detail: string;
}

export const FACTS: readonly FactItem[] = [
  {
    value: '1',
    unit: 'Hz',
    label: '遥测上报频率',
    detail: '机体侧 MAVLink 约定为每秒一帧。平台默认值与之一致，不做没有意义的提速。',
  },
  {
    value: '3000',
    unit: 'ms',
    label: '断链判定',
    detail: '超过该时长未收到新数据即判定为失联，不会以历史数据继续渲染界面。',
  },
  {
    value: '180',
    unit: '天',
    label: '审计保留',
    detail: '何人、何时、下发何指令、变更何配置，均留档，默认保留半年。',
  },
  {
    value: '4',
    unit: '类角色',
    label: '权限分层',
    detail: '管理员、操作员、维护员、观察员。前端与后端共用同一份权限定义。',
  },
];

export interface Capability {
  id: string;
  icon: IconName;
  title: string;
  summary: string;
  points: readonly string[];
}

/** 六大模块与 `platform/frontend/src/app/nav.ts` 一一对应。 */
export const CAPABILITIES: readonly Capability[] = [
  {
    id: 'fleet',
    icon: 'fleet',
    title: '机队总览',
    summary: '将全部机体的状态汇聚于一屏，状态异常的机体自动进入您的视野。',
    points: ['地图上的位置与航迹', '电量进度、链路质量分级', '由卡片直接进入单机管理'],
  },
  {
    id: 'registry',
    icon: 'registry',
    title: '无人机管理',
    summary: '一份设备档案，决定后续所有链路能否建立。',
    points: ['支持整支机队批量导入', '每台机体独立接入凭据', '端点字段与机型适配配置'],
  },
  {
    id: 'tracking',
    icon: 'tracking',
    title: '单机管理',
    summary: '单台机体的全部信息与全部操作，集中于同一页面。',
    points: ['地图、姿态球与大数字指标', '完整的遥测数据列表', '指令下发前先经过条件检查清单', '全屏模式，供值班人员整屏值守'],
  },
  {
    id: 'users',
    icon: 'users',
    title: '用户管理',
    summary: '权限并非写在备注中，而是前后端共用的一份定义。',
    points: ['四种内置角色覆盖常见分工', '界面收敛入口，Worker 强制执行鉴权', '首位管理员登录后须修改初始密码'],
  },
  {
    id: 'audit',
    icon: 'audit',
    title: '审计日志',
    summary: '发生事故时能够查证责任归属 —— 这是监管场景的底线。',
    points: ['敏感操作逐条留档', '可按时间与操作人检索', '默认保留 180 天'],
  },
  {
    id: 'settings',
    icon: 'settings',
    title: '系统设置',
    summary: '频率与阈值均可调整，不同机队无需共用同一套假设。',
    points: ['遥测频率与断链阈值', '三级告警阈值支持逐层覆盖', '平台名称与探测开关'],
  },
];

export interface PlaneContent {
  id: string;
  icon: IconName;
  role: string;
  title: string;
  subtitle: string;
  responsibilities: readonly string[];
}

export const PLANES: readonly PlaneContent[] = [
  {
    id: 'control',
    icon: 'shield',
    role: '控制面',
    title: '平台 Worker',
    subtitle: '账号、权限、设备档案、令牌签发、审计',
    responsibilities: [
      '承担登录校验与角色权限判定',
      '保存设备档案，加密存储接入凭据',
      '按需签发短期访问令牌',
      '写入并检索审计日志',
    ],
  },
  {
    id: 'data',
    icon: 'link',
    role: '数据面',
    title: '浏览器 ⇄ 机体直连',
    subtitle: '遥测经 WebSocket 推送，链路异常时自动降级为轮询',
    responsibilities: [
      '遥测与姿态数据不经过平台转发',
      '每台机体独立地址，可单独替换',
      '断链、慢响应与凭据失效均有明确状态',
      '同一份接口约定，真机与模拟机通用',
    ],
  },
];

export const PLANE_TRADEOFF: readonly { label: string; text: string }[] = [
  {
    label: '收益',
    text: '高频遥测不占用平台的带宽与算力，因此投入集中在「管得住」，而非「转得多」。',
  },
  {
    label: '代价',
    text: '机体须可被浏览器访问；HTTPS 页面下还须为机体配置 TLS。若条件不满足，界面会明确指出原因，而非静默失败。',
  },
];

export interface AccessPath {
  id: string;
  icon: IconName;
  eyebrow: string;
  title: string;
  summary: string;
  steps: readonly { title: string; text: string }[];
  footnote: string;
}

export const ACCESS_PATHS: readonly AccessPath[] = [
  {
    id: 'developer',
    icon: 'code',
    eyebrow: '个人开发者与 DIY',
    title: '实现一份公开的接口约定，即可将机体纳入平台监管',
    summary:
      '平台与机体之间仅有两条通路：一条用于获取令牌，一条用于建立直连。机体侧按文档实现四类接口即可，平台侧无需改动任何代码。',
    steps: [
      { title: '实现接口', text: '探活、遥测推送、指令接收与告警上报四类接口，路径与返回格式依照文档实现。' },
      { title: '登记设备', text: '在「无人机管理」中填写端点信息，或导入机队档案批量建档。' },
      { title: '选择凭据方式', text: '长期 API Key、平台签发的短期令牌，或依据网络隔离而不下发凭据，三者皆可。' },
      { title: '接入并查看', text: '设备在总览中逐台上线，位置、航迹、电量与链路质量即时进入地图。' },
    ],
    footnote: '自研机体、开源飞控改装机与教学用小型机体均可接入，无需采购任何指定硬件。',
  },
  {
    id: 'enterprise',
    icon: 'building',
    eyebrow: '企业与机构',
    title: '部署至自有账号，或按业务流程定制',
    summary:
      '平台本体是一套可独立部署的应用（Workers + D1 + Pages）。既可部署至贵方自有域名与账号，也可按既有业务流程进行改造。',
    steps: [
      { title: '私有化部署', text: '前后端一并部署至贵方 Cloudflare 账号，数据与账号体系完全由贵方掌握。' },
      { title: '品牌与流程定制', text: '界面命名、角色划分、审批与值守流程，均可按贵方现行方式调整。' },
      { title: '对接既有系统', text: '设备档案、遥测与审计数据均可按接口对接至贵方在用的业务系统。' },
      { title: '配套软硬件', text: '从机体到平台是一条完整的路径，如有需要可一并商议。' },
    ],
    footnote: '我们不销售无人机整机，仅提供平台与配套软硬件的方案与定制服务。',
  },
];

export interface HomeSolution {
  id: string;
  icon: IconName;
  title: string;
  text: string;
}

export const HOME_SOLUTIONS: readonly HomeSolution[] = [
  { id: 'park', icon: 'pin', title: '园区与起降场', text: '进出场记录、在场机体与作业空域，呈现于同一张图上。' },
  { id: 'inspection', icon: 'route', title: '巡检作业队', text: '多机组并行作业时，位置、链路与告警一屏尽览。' },
  { id: 'research', icon: 'graduation', title: '科研与教学', text: '以模拟机队完成全部实操环节，无需消耗真机。' },
  { id: 'diy', icon: 'cpu', title: '个人开发者与 DIY', text: '自研机体按文档接入，同样具备企业级机队的监管能力。' },
  { id: 'custom', icon: 'layers', title: '企业定制 SaaS', text: '依据贵方业务流程，将平台改造为契合自身运营的形态。' },
];

export interface CtaContent {
  eyebrow: string;
  title: string;
  text: string;
  actions: readonly ActionLink[];
}

export const HOME_CTA: CtaContent = {
  eyebrow: '下一步',
  title: '先看清平台本身，再决定如何使用',
  text: '直接进入平台查看机队，比阅读十页介绍更为直观。若有具体场景需要讨论，欢迎来信。',
  actions: [
    { label: '进入平台', to: 'https://havorix.work', external: true, variant: 'primary' },
    { label: '申请试用', to: '/pricing#trial', variant: 'secondary' },
  ],
};
